import { useCallback, useRef } from "react";
import { useUi } from "../state/ui";
import { useBlockFrames } from "../sync/hooks";
import { minimapProjection, minimapToCanvas } from "../ui/minimap";

const SIZE = { width: 168, height: 108 };

/** Overview of the whole board with the current viewport; click to jump there. */
export function Minimap({ viewportSize }: { viewportSize: () => { width: number; height: number } }) {
  const frames = useBlockFrames();
  const camera = useUi((state) => state.camera);
  const setCamera = useUi((state) => state.setCamera);
  const surface = useRef<HTMLDivElement>(null);

  const projection = minimapProjection(
    frames.map((frame) => ({ x: frame.x, y: frame.y, w: frame.w, h: frame.h })),
    camera,
    viewportSize(),
    SIZE,
  );

  const jumpTo = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!projection) return;
      const rect = surface.current?.getBoundingClientRect();
      if (!rect) return;
      setCamera(
        minimapToCanvas(
          { x: event.clientX - rect.left, y: event.clientY - rect.top },
          projection,
          camera,
          viewportSize(),
        ),
      );
    },
    [projection, camera, setCamera, viewportSize],
  );

  if (!projection) return null;

  return (
    <div
      ref={surface}
      className="minimap"
      style={{ width: SIZE.width, height: SIZE.height }}
      onPointerDown={jumpTo}
      onPointerMove={(event) => event.buttons === 1 && jumpTo(event)}
      title="Klicken, um an diese Stelle zu springen"
    >
      {projection.blocks.map((rect, index) => (
        <div
          key={frames[index]?.id ?? index}
          className="minimap__block"
          style={{
            left: rect.x,
            top: rect.y,
            width: Math.max(rect.w, 2),
            height: Math.max(rect.h, 2),
            background: frames[index]?.accent ?? "var(--ink-faint)",
          }}
        />
      ))}
      <div
        className="minimap__viewport"
        style={{
          left: projection.viewport.x,
          top: projection.viewport.y,
          width: Math.max(projection.viewport.w, 6),
          height: Math.max(projection.viewport.h, 6),
        }}
      />
    </div>
  );
}
